import { useSTKContext } from '@/context/context';
import { CellContext } from '@tanstack/react-table';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/Button/Button';
import { ButtonVariants } from '../../../components/Button/Button.types';
import Header from '../../../components/Header/Header';
import Link from '../../../components/Link/Link';
import TableCardGlobalSearch from '../../../components/Tables/TableCardGlobalSearch/TableCardGlobalSearch';
import { useStores } from '../../../services/stores/stores.service';
import { Store } from '../../../types/stores.types';
import styles from './Menus.module.css';

export const LinkCell = ({ row }: CellContext<Store, unknown>) => {
  const { setStores } = useSTKContext();
  return (
    <Link onClick={() => setStores([row.original.storeNo])} to={`/menus/${row.original.storeNo}`}>
      {row.original.storeNo}
    </Link>
  );
};

const Menus: FC = () => {
  const { t } = useTranslation();
  const { setStores } = useSTKContext();
  const { data: axiosResponse, isLoading, isError } = useStores(true);
  const [selectedRows, setSelectedRows] = useState<{ [number: number]: boolean }>({});
  const [filteredData, setFilteredData] = useState<Store[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (axiosResponse?.data) {
      setFilteredData(axiosResponse.data);
    }
  }, [axiosResponse?.data]);

  if (isError) {
    return <div>Error loading data</div>;
  }
  if (isLoading || !axiosResponse || axiosResponse.status !== 200 || !axiosResponse.data) {
    return <div>Loading...</div>;
  }

  const storeData: Store[] = axiosResponse.data;
  return (
    <>
      <Header
        title={t('menu.titleAdmin')}
        breadcrumb={{ to: '/', label: t('breadcrumb.backToHome') }}
        includeReportsLink
      />

      <div className={styles.table}>
        <div className={styles.headerTwo}>
          <div className={styles.leftHeaderLabel}>
            <div className={styles.storeMenusText}>{t('menu.selectStoreLabel')}</div>
            <div className={styles.warnMessage}>{t('menu.forBestPerformanceSelect20')}</div>
          </div>
          <div>
            <Button
              variant={ButtonVariants.Primary}
              onClick={() => {
                const storeList = Object.keys(selectedRows);
                setStores(storeList);
                navigate('/menus/manage-menus');
              }}
              disabled={Object.keys(selectedRows).length < 1}
            >
              {t('menu.manageMenuButton')}
            </Button>
          </div>
        </div>

        <TableCardGlobalSearch
          includeMinWidth={false}
          returnedRecordsText={(count) => t('menu.table.returnedRecord', { count })}
          enableRowSelection
          selectedRows={selectedRows}
          isLoading={isLoading}
          data={storeData}
          setFilteredData={setFilteredData}
          columns={[
            {
              id: 'storeNo',
              accessorKey: 'storeNo',
              cell: LinkCell,
              header: t('menu.table.storeId'),
              enableSorting: true,
            },
            { id: 'fzCode', accessorKey: 'fzCode', header: t('menu.table.fzCode'), enableSorting: true },
          ]}
          onRowSelectionChange={setSelectedRows}
        />
      </div>
    </>
  );
};

export default Menus;
